import type { Rng } from '../core/rng';
import type { Vec2, PolygonWithHoles } from './types';
import { ringBounds } from './polygon';
import { bandRing, inBandedRing } from './bandedRing';

const DEFAULT_MAX_ATTEMPTS = 30;

export function poissonInPolygon(
  poly: PolygonWithHoles,
  radius: number,
  rng: Rng,
  maxAttempts: number = DEFAULT_MAX_ATTEMPTS
): readonly Vec2[] {
  const bounds = ringBounds(poly.outer);
  const outer = bandRing(poly.outer),
    holes = poly.holes.map(bandRing);
  const inside = (p: Vec2) =>
    inBandedRing(p, outer) && !holes.some((hole) => inBandedRing(p, hole));
  const cellSize = radius / Math.SQRT2;

  const gridWidth = Math.ceil((bounds.maxX - bounds.minX) / cellSize);
  const gridHeight = Math.ceil((bounds.maxY - bounds.minY) / cellSize);
  const grid: (Vec2 | null)[] = new Array(gridWidth * gridHeight).fill(null);

  function gridIndex(x: number, y: number): number {
    const cx = Math.floor((x - bounds.minX) / cellSize);
    const cy = Math.floor((y - bounds.minY) / cellSize);
    return cy * gridWidth + cx;
  }

  function isValidPoint(p: Vec2): boolean {
    if (!inside(p)) return false;

    const cx = Math.floor((p[0] - bounds.minX) / cellSize);
    const cy = Math.floor((p[1] - bounds.minY) / cellSize);

    const searchRadius = 2;
    for (let dy = -searchRadius; dy <= searchRadius; dy++) {
      for (let dx = -searchRadius; dx <= searchRadius; dx++) {
        const nx = cx + dx;
        const ny = cy + dy;
        if (nx < 0 || nx >= gridWidth || ny < 0 || ny >= gridHeight) continue;

        const neighbor = grid[ny * gridWidth + nx];
        if (neighbor) {
          const distSq =
            (neighbor[0] - p[0]) * (neighbor[0] - p[0]) +
            (neighbor[1] - p[1]) * (neighbor[1] - p[1]);
          if (distSq < radius * radius) return false;
        }
      }
    }

    return true;
  }

  function generateRandomPointInAnnulus(center: Vec2): Vec2 {
    const r = radius * (1 + rng.next());
    const theta = rng.next() * 2 * Math.PI;
    return [center[0] + r * Math.cos(theta), center[1] + r * Math.sin(theta)];
  }

  const samples: Vec2[] = [];
  const active: Vec2[] = [];

  let initialPoint: Vec2 | null = null;
  for (let attempt = 0; attempt < 100; attempt++) {
    const x = bounds.minX + rng.next() * (bounds.maxX - bounds.minX);
    const y = bounds.minY + rng.next() * (bounds.maxY - bounds.minY);
    const p: Vec2 = [x, y];
    if (inside(p)) {
      initialPoint = p;
      break;
    }
  }

  if (!initialPoint) return [];

  samples.push(initialPoint);
  active.push(initialPoint);
  grid[gridIndex(initialPoint[0], initialPoint[1])] = initialPoint;

  while (active.length > 0) {
    const randomIndex = Math.floor(rng.next() * active.length);
    const center = active[randomIndex]!;

    let found = false;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const candidate = generateRandomPointInAnnulus(center);
      if (
        candidate[0] >= bounds.minX &&
        candidate[0] <= bounds.maxX &&
        candidate[1] >= bounds.minY &&
        candidate[1] <= bounds.maxY &&
        isValidPoint(candidate)
      ) {
        samples.push(candidate);
        active.push(candidate);
        grid[gridIndex(candidate[0], candidate[1])] = candidate;
        found = true;
        break;
      }
    }

    if (!found) {
      active.splice(randomIndex, 1);
    }
  }

  return samples;
}
